import { FormControl, FormField, FormItem } from '@/components/primitives/Form';
import { useFormField } from '@/components/primitives/useFormField';
import { cn } from '@/utils/cn';
import { Select } from '@packages/webshop-design-system';
import React from 'react';
import {
  useFormContext,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<SelectFieldProps<TFieldValues, TName>, 'name'> {
  field: ControllerRenderProps<TFieldValues, TName>;
}

const SelectFieldContent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field,
  label,
  hideLabel,
  options,
  placeholder,
  classNames,
  required,
  'aria-label': ariaLabel,
}: SelectFieldContentProps<TFieldValues, TName>) => {
  const { error, formItemId } = useFormField();

  return (
    <FormItem className={classNames?.wrapper}>
      <FormControl label={label} required={required} hideLabel={hideLabel}>
        <Select
          options={options}
          selectedKey={field.value ?? null}
          onSelectionChange={(key) => field.onChange(key as string)}
          placeholder={placeholder}
          error={!!error}
          className={cn(classNames?.select)}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : `${formItemId}-label`}
        />
      </FormControl>
    </FormItem>
  );
};

export interface SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> {
  label: React.ReactNode;
  name: TName;
  hideLabel?: boolean;
  options: SelectOption[];
  placeholder?: string;
  classNames?: { wrapper?: string; select?: string };
  required?: boolean;
  'aria-label'?: string;
}

export const SelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  name,
  ...props
}: SelectFieldProps<TFieldValues, TName>) => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => <SelectFieldContent field={field} {...props} />}
    />
  );
};

SelectField.displayName = 'SelectField';
